import React from 'react';
import { get, isNumber, orderBy } from 'lodash';
import { metricKeys } from '@pulse/metrics';
import { ApiOutlined, CopyOutlined } from '@ant-design/icons';
import { useAtom, useAtomValue } from 'jotai';
import copy from 'copy-to-clipboard';
import { Button, Tooltip, message } from 'antd';
// TODO: cross importing like this from dimension-panel-hz is not a good practice
// a dev working on that component wouldn't know it's used here
// if you want to reuse a component, move it to a common folder and then import it
import ValueCell from '@components/dimension-panel-hz/dimension-table/ValueCell';
import {
  DIMENSION_COL_WIDTH,
  METRIC_COL_WIDTH,
  SF_COL_WIDTH,
  getMetricFilterMetric,
} from '@components/dimension-panel-hz/dimension-table/utils';
import MetricHeader from '@components/dimension-panel-hz/dimension-table/MetricHeader';
import { MF_LABEL } from '@components/metrics-panel-hz/utils/metricFilterUtils';
import { LOCAL_METRIC_SWITCH_MAP } from '@utils/metrics';
import Header from '@components/dimension-panel-hz/dimension-table/Header';
import { UNKNOWN_NAME } from '@utils/constants';
import {
  ActionBar,
  StyledEllipsis,
  StyledEllipsisTooltip,
} from '@components/dimension-panel-hz/dimension-table/styled';
import { getMetricMetaByKey } from '@src/utils/metrics/metricHelpers';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { DrilldownItem, drillDownFiltersAtom, drillDownListAtom, traceIdInfoAtom } from './atom';
import {
  ETableMode,
  calcPercentage,
  dimensionTraceId,
  getPctMetric,
  getSSFPctMetric,
  hintColorMap,
  metricsToCallSSFPct,
  networkRequestUrlDimensions,
} from './constant';
import { RCADrillDownToEventScope } from '../RCADrillDownToEventScope';
import { HintCellWrapper, HintTagWrapper } from '../styled';

const COLUMN_HEADER_METRIC_HELPTEXT = {};
const COLUMN_HEADER_METRIC_FILTER_HELPTEXT = {};
const COLUMN_HEADER_METRIC_FILTER_PCT_HELPTEXT = {
  // [metricKeys.AverageRequestDuration]: 'Impacted Network Request Duration based on top 1k impacted sessions//Pages',
  // [metricKeys.AppUniqueDevices]: 'Impacted Devices based on top 1k impacted sessions',
  // [metricKeys.AverageRequestCount]: 'Impacted Network Request Count based on top 1k impacted sessions/Pages'
};
const addMetricFilterTag = (row = {}) => {
  const taggedRow = {};
  for (const [key, value] of Object.entries(row)) {
    taggedRow[getMetricFilterMetric(key)] = value;
  }
  return taggedRow;
};

interface ICellProps {
  cellData: any;
  column: any;
  rowData: any;
  mode?: ETableMode;
  canNavToPreset?: boolean;
  onClick?: (info: any) => void;
}

const Cell: React.FC<ICellProps> = ({
  cellData,
  column,
  rowData,
  mode = ETableMode.drilldown,
  canNavToPreset = false,
}) => {
  const dimension = column.key;
  const [drilldownList, setDrilldownList] = useAtom(drillDownListAtom);
  const [traceIdInfo, setTraceIdInfo] = useAtom(traceIdInfoAtom);
  const applyRules = useAtomValue(drillDownFiltersAtom);
  const handleClick = () => {
    if (!rowData.disableDrilldown) {
      switch (mode) {
        case ETableMode.drilldown: {
          const name = get(rowData, 'name[0]', '');
          const value = get(rowData, 'value[0]', '');
          const drilldownRule = {
            field: dimension,
            key: dimension,
            op: 'eq',
            value,
            display: name,
            originalField: dimension,
          };
          const hasExist = applyRules.some((it) => {
            return it.length === 1 && it[0].field === dimension && it[0].value === value;
          });
          if (!hasExist) {
            const crumbRules = getRulesFromFilter([[drilldownRule]], true);
            const crumbFilter = getFilterFromRules(crumbRules, true);
            setDrilldownList([
              ...drilldownList,
              { name: crumbFilter.name, rule: crumbFilter.rule } as DrilldownItem,
            ]);
          }
          // dispatch(dimensionDrilldown(dimension, rowData));
          break;
        }
      }
    }
  };

  const handleActions = (value) => {
    if (value === 'copy') {
      return () => {
        if (copy(cellData)) {
          message.info('Copied to clipboard');
        }
      };
    }
    if (value === 'traceId') {
      return () => {
        setTraceIdInfo({
          ...traceIdInfo,
          open: true,
          info: {
            [networkRequestUrlDimensions.host]: rowData[networkRequestUrlDimensions.host],
            [networkRequestUrlDimensions.path]: rowData[networkRequestUrlDimensions.path],
            [networkRequestUrlDimensions.query]: rowData[networkRequestUrlDimensions.query],
          },
        });
      };
    }
    return () => null;
  };

  const styles = rowData.disableDrilldown ? { pointerEvents: 'none' } : {};
  const actions = [{ label: <CopyOutlined />, value: 'copy' }];
  if (mode === ETableMode.showDrawer) {
    actions.push({ label: <ApiOutlined />, value: 'traceId' });
  }

  return (
    <>
      <StyledEllipsis onClick={handleClick} style={styles}>
        <StyledEllipsisTooltip text={cellData} />
      </StyledEllipsis>
      <Actions
        actions={actions}
        handleActions={handleActions}
        rowData={rowData}
        canNavToPreset={canNavToPreset}
      />
    </>
  );
};

function Actions({ actions, handleActions, rowData, canNavToPreset }) {
  return (
    <ActionBar className="action-bar">
      {canNavToPreset && <RCADrillDownToEventScope rowData={rowData} />}
      {actions.map((action) => {
        if (action.value === 'traceId') {
          return (
            <Tooltip title="Show Trace ID" key={action.value}>
              <Button
                key={action.value}
                type="text"
                icon={action.label}
                onClick={handleActions(action.value)}
              />
            </Tooltip>
          );
        }
        return (
          <Button
            key={action.value}
            type="text"
            icon={action.label}
            onClick={handleActions(action.value)}
          />
        );
      })}
    </ActionBar>
  );
}

export function formatMultiData({ data = {}, sortBy, searchText }) {
  const { metadataFilterData = {}, metricFilterData = {} }: any = data;

  // move unknown rows to bottom
  let orderedRows: any = [];
  const nanRows: unknown[] = [];
  const unknownRows: any = [];
  let totals = {};

  if (
    get(metadataFilterData, ['rows'], []).length > 0 &&
    get(metricFilterData, ['rows'], []).length > 0
  ) {
    totals = {
      name: ['Total'],
      ...metadataFilterData.totals,
      ...addMetricFilterTag(metricFilterData.totals),
      disableDrilldown: true,
    };
    for (let i = 0; i < metadataFilterData.rows.length; i++) {
      const rawData = metadataFilterData.rows[i];
      const sfData = addMetricFilterTag(get(metricFilterData, ['rows', i], {}));

      // flattened
      const row = {
        ...rawData,
        ...sfData,
      };

      /** Calculate percetage value for metric in metricsToCallSSFPct */
      for (const metric of metricsToCallSSFPct) {
        const sfTotal = totals[getMetricFilterMetric(metric)];
        const total = totals[metric];
        const ssfPctKey = getSSFPctMetric(metric);
        const pctKey = getPctMetric(metric);
        const sfValue = row[getMetricFilterMetric(metric)];
        row[ssfPctKey] = calcPercentage(sfTotal, sfValue);
        const value = row[metric];
        row[pctKey] = calcPercentage(total, value);
      }

      const name = row.name.join('');
      if (UNKNOWN_NAME.includes(name)) {
        unknownRows.push({ ...row, disableDrilldown: false });
        // Filter out all NaN, such as "NA"
        // because the presence of NA will cause _.orderBy not to work properly
      } else if (sortBy?.key && !isNumber(get(row, sortBy.key))) {
        nanRows.push(row);
      } else {
        orderedRows.push(row);
      }
    }

    if (sortBy) {
      orderedRows = orderBy(orderedRows, [sortBy.key], [sortBy.order]);
    }
  }

  let filteredRows = orderedRows.concat(nanRows, unknownRows);

  /** Calculate total percentage value */
  for (const metric of metricsToCallSSFPct) {
    const ssfPctKey = getSSFPctMetric(metric);
    const pctKey = getPctMetric(metric);
    const { sfValue, value } = filteredRows.reduce(
      (sum, row) => {
        const sfValue = row[getMetricFilterMetric(metric)];
        const value = row[metric];
        if (isNumber(sfValue)) {
          sum.sfValue += sfValue;
        }
        if (isNumber(value)) {
          sum.value += value;
        }
        return sum;
      },
      {
        sfValue: 0,
        value: 0,
      },
    );
    totals[ssfPctKey] = sfValue === 0 ? 0 : 100;
    totals[pctKey] = value === 0 ? 0 : 100;
  }
  // search
  if (searchText) {
    const regex = new RegExp(searchText, 'i'); // ignore case
    filteredRows = filteredRows.filter((row) => regex.test(row.name.join('')));
  }

  return { rows: filteredRows, totals };
}

export const formatMultiColumns = ({ dimension, metrics, showNAForMetric }) => {
  const columnConfig: any[] = [];

  // first column is the dimension value
  columnConfig.push({
    title: '',
    dataKey: 'name',
    key: dimension,
    width:
      dimension === dimensionTraceId
        ? DIMENSION_COL_WIDTH + METRIC_COL_WIDTH * 2
        : DIMENSION_COL_WIDTH,
    minWidth: DIMENSION_COL_WIDTH,
    frozen: 'left',
    resizable: true,
    disableDrilldown: false,
    cellRenderer: ({ cellData, column, rowData }) => (
      <Cell cellData={cellData} column={column} rowData={{ ...rowData, disableDrilldown: false }} />
    ),
    headerRenderer: (props) => <Header {...props} />,
  });

  // remaining columns are the metric values
  metrics.forEach((metric, idx) => {
    const metricInfo = getMetricMetaByKey({ metricKey: metric });
    const showNA = showNAForMetric ? showNAForMetric(metric) : false;
    const evenCol = (idx + 1) % 2 === 0;
    columnConfig.push({
      title: metricInfo.short,
      dataKey: metric,
      key: metric,
      metric,
      metricInfo,
      flexGrow: 1,
      align: 'right',
      width: METRIC_COL_WIDTH,
      minWidth: METRIC_COL_WIDTH,
      sortable: true,
      cellRenderer: ({ cellData, column, container, rowData }) => (
        <ValueCell
          cellData={showNA ? 'NA' : cellData}
          column={column}
          container={container}
          rowData={rowData}
        />
      ),
      headerRenderer: (props) => (
        <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_HELPTEXT} />
      ),
      className: evenCol ? 'table-zebra-even' : 'table-zebra-odd',
    });

    const mfMetricAbs = getMetricFilterMetric(metric);
    // const mfMetricPct = getMetricFilterPctMetric(metric);
    const ssfPct = getSSFPctMetric(metric);
    const pct = getPctMetric(metric);
    const mfTitle = `[${MF_LABEL}]`;

    /** AverageRequestCount and AppUniqueDevices Do not have ssf column */
    if (![metricKeys.AverageRequestCount, metricKeys.AppUniqueDevices].includes(metric)) {
      columnConfig.push({
        title: mfTitle,
        dataKey: mfMetricAbs,
        key: mfMetricAbs,
        metric,
        metricInfo,
        flexGrow: 1,
        align: 'right',
        width: SF_COL_WIDTH,
        minWidth: SF_COL_WIDTH,
        sortable: true,
        mfPctChecked: false, // indicator to always use unit as % when %MF is selected
        cellRenderer: ({ cellData, column, container, rowData }) => (
          <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
        ),
        headerRenderer: (props) => (
          <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_FILTER_HELPTEXT} />
        ),
        headerClassName: 'sf-highlight',
        className: evenCol ? 'table-zebra-even' : 'table-zebra-odd',
      });
    }

    /** AverageRequestDuration Do not have ssf percentage column */
    if (metric !== metricKeys.AverageRequestDuration) {
      columnConfig.push({
        title: metricInfo.short + ' %',
        dataKey: pct,
        key: pct,
        metric,
        metricInfo,
        flexGrow: 1,
        align: 'right',
        width: METRIC_COL_WIDTH,
        minWidth: METRIC_COL_WIDTH,
        sortable: true,
        mfPctChecked: true, // indicator to always use unit as % when %MF is selected
        cellRenderer: ({ cellData, column, container, rowData }) => (
          <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
        ),
        headerRenderer: (props) => (
          <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_FILTER_PCT_HELPTEXT} />
        ),
        className: evenCol ? 'table-zebra-even' : 'table-zebra-odd',
      });
      columnConfig.push({
        title: mfTitle + ' %',
        dataKey: ssfPct,
        key: ssfPct,
        metric,
        metricInfo,
        flexGrow: 1,
        align: 'right',
        width: SF_COL_WIDTH,
        minWidth: SF_COL_WIDTH,
        sortable: true,
        mfPctChecked: true, // indicator to always use unit as % when %MF is selected
        cellRenderer: ({ cellData, column, container, rowData }) => (
          <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
        ),
        headerRenderer: (props) => (
          <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_FILTER_PCT_HELPTEXT} />
        ),
        headerClassName: 'sf-highlight',
        className: evenCol ? 'table-zebra-even' : 'table-zebra-odd',
      });
    }
  });

  return columnConfig;
};

const getMetricInfo = (metricList, metricName) => {
  return metricList[metricName] || metricList[LOCAL_METRIC_SWITCH_MAP[metricName]];
};

export const formatColumns = ({ dimension, metrics, showNAForMetric }) => {
  const columnConfig: any[] = [];
  // first column is the dimension value
  columnConfig.push({
    title: '',
    dataKey: 'name',
    key: dimension,
    width:
      dimension === dimensionTraceId
        ? DIMENSION_COL_WIDTH + METRIC_COL_WIDTH * 2
        : DIMENSION_COL_WIDTH,
    minWidth: DIMENSION_COL_WIDTH,
    frozen: 'left',
    resizable: true,
    disableDrilldown: true,
    cellRenderer: ({ cellData, column, rowData }) => (
      <Cell cellData={cellData} column={column} rowData={{ ...rowData, disableDrilldown: true }} />
    ),
    headerRenderer: (props) => <Header {...props} />,
  });

  // remaining columns are the metric values
  metrics.forEach((metric) => {
    const metricInfo = getMetricMetaByKey({ metricKey: metric });
    const showNA = showNAForMetric ? showNAForMetric(metric) : false;
    columnConfig.push({
      title: metricInfo.short,
      dataKey: metric,
      key: metric,
      metric,
      metricInfo,
      flexGrow: 1,
      align: 'right',
      width: METRIC_COL_WIDTH,
      minWidth: METRIC_COL_WIDTH,
      sortable: true,
      cellRenderer: ({ cellData, column, container, rowData }) => {
        return (
          <ValueCell
            cellData={showNA ? 'NA' : cellData}
            column={column}
            container={container}
            rowData={rowData}
          />
        );
      },
      headerRenderer: (props) => <MetricHeader {...props} />,
    });
  });

  return columnConfig;
};

export const genNetworkRequestUrl = (item) => {
  const host = item[networkRequestUrlDimensions.host] ?? '*';
  const path = item[networkRequestUrlDimensions.path] ?? '/*';
  const query = item[networkRequestUrlDimensions.query] ?? '';
  return `${host}${path}${query}`;
};

export const genHints = (item, rcaData) => {
  if (!rcaData) {
    return [];
  }
  const rcaRes = rcaData.find(
    (d) =>
      d.outlier[`event.${networkRequestUrlDimensions.host}`] ===
        item[networkRequestUrlDimensions.host] &&
      d.outlier[`event.${networkRequestUrlDimensions.path}`] ===
        item[networkRequestUrlDimensions.path] &&
      d.outlier[`event.${networkRequestUrlDimensions.query}`] ===
        item[networkRequestUrlDimensions.query],
  );
  return rcaRes?.algorithms ?? [];
};

export const addRCADimension = (_rows, rcaData) => {
  return _rows.map((item) => {
    return {
      ...item,
      network_api_calls: genNetworkRequestUrl(item),
      hints: genHints(item, rcaData),
    };
  });
};

export const formatRCAColumns = ({ dimension, metrics }) => {
  const columnConfig: any[] = [];

  // first column is the dimension value
  columnConfig.push({
    title: 'Network API Calls',
    dataKey: dimension,
    key: dimension,
    width: DIMENSION_COL_WIDTH + METRIC_COL_WIDTH * 2,
    minWidth: DIMENSION_COL_WIDTH,
    frozen: 'left',
    resizable: true,
    disableDrilldown: false,
    cellRenderer: ({ cellData, column, rowData }) => (
      <Cell
        cellData={cellData}
        column={column}
        rowData={{ ...rowData, disableDrilldown: false }}
        mode={ETableMode.showDrawer}
        canNavToPreset={true}
      />
    ),
  });

  // remaining columns are the metric values
  metrics.forEach((metric) => {
    const metricInfo = getMetricMetaByKey({ metricKey: metric });

    const mfMetricAbs = getMetricFilterMetric(metric);
    const ssfPct = getSSFPctMetric(metric);
    const mfTitle = `[${MF_LABEL}] ` + metricInfo.short;

    /** AverageRequestDuration Do not have ssf percentage column */
    if (metric !== metricKeys.AverageRequestDuration) {
      columnConfig.push({
        title: mfTitle + ' %',
        dataKey: ssfPct,
        key: ssfPct,
        metric,
        metricInfo,
        flexGrow: 1,
        align: 'right',
        width: METRIC_COL_WIDTH,
        minWidth: METRIC_COL_WIDTH,
        sortable: true,
        mfPctChecked: true, // indicator to always use unit as % when %MF is selected
        cellRenderer: ({ cellData, column, container, rowData }) => (
          <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
        ),
        headerRenderer: (props) => (
          <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_FILTER_PCT_HELPTEXT} />
        ),
      });
    }

    /** AverageRequestCount and AppUniqueDevices Do not have ssf column */
    if (!metricsToCallSSFPct.includes(metric)) {
      columnConfig.push({
        title: mfTitle,
        dataKey: mfMetricAbs,
        key: mfMetricAbs,
        metric,
        metricInfo,
        flexGrow: 1,
        align: 'right',
        width: METRIC_COL_WIDTH,
        minWidth: METRIC_COL_WIDTH,
        sortable: true,
        mfPctChecked: false, // indicator to always use unit as % when %MF is selected
        cellRenderer: ({ cellData, column, container, rowData }) => (
          <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
        ),
        headerRenderer: (props) => (
          <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_FILTER_HELPTEXT} />
        ),
      });
    }
  });

  // hints
  columnConfig.push({
    title: 'Reason',
    dataKey: 'hints',
    key: 'hints',
    flexGrow: 1,
    align: 'right',
    width: METRIC_COL_WIDTH + 30,
    minWidth: METRIC_COL_WIDTH + 30,
    cellRenderer: ({ cellData }) => <HintCell cellData={cellData} />,
  });

  return columnConfig;
};

const HintTag = ({ tag }) => {
  const color = hintColorMap[tag?.label];
  if (!tag.reason || !tag.label) {
    return <div />;
  }
  return (
    <Tooltip title={tag.reason} placement="topRight">
      <HintTagWrapper color={color}>{tag.label}</HintTagWrapper>
    </Tooltip>
  );
};

const HintCell = ({ cellData }) => {
  cellData.sort((a, b) => {
    return a.label.localeCompare(b.label);
  });
  return (
    <HintCellWrapper>
      {cellData.map((tag) => (
        <HintTag key={`${tag.label}/${tag.reason}`} tag={tag} />
      ))}
    </HintCellWrapper>
  );
};
